/**
 * G.A.C.A. (Generative AI Cost Arbitrage) Client Service
 *
 * Connects to the G.A.C.A. multi-provider AI system for intelligent
 * model selection and automatic failover across 7+ AI providers.
 */

const GACA_API_URL = '';

export interface GACARequest {
  prompt: string;
  systemPrompt?: string;
  maxTokens?: number;
  temperature?: number;
  preferredProvider?: string;
}

export interface GACAResponse {
  content: string;
  model: string;
  provider: string;
  tokensUsed?: number;
  latencyMs: number;
}

export interface GACAStatusResponse {
  status: 'healthy' | 'degraded' | 'down';
  activeProviders: number;
  totalProviders: number;
  providers: ProviderStatus[];
  timestamp: string;
  avgLatencyMs?: number;
  totalRequestsToday?: number;
}

export interface ProviderStatus {
  name: string;
  status: 'online' | 'offline' | 'rate_limited';
  latencyMs?: number;
  usagePercent?: number;
}

export interface GACAHealthResponse {
  status: 'ok' | 'error' | 'healthy' | 'degraded';
  version?: string;
  uptime?: number;
  providersCount?: number;
  healthyProvidersCount?: number;
}

export class GACAError extends Error {
  constructor(
    message: string,
    public statusCode?: number,
    public provider?: string
  ) {
    super(message);
    this.name = 'GACAError';
  }
}

/**
 * Send a completion request to G.A.C.A.
 */
export async function completeWithGACA(request: GACARequest): Promise<GACAResponse> {
  const startTime = Date.now();

  try {
    const response = await fetch(`${GACA_API_URL}/api/chat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        prompt: request.prompt,
        systemPrompt: request.systemPrompt || getDefaultSystemPrompt(),
        maxTokens: request.maxTokens || 1024,
        temperature: request.temperature ?? 0.7,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new GACAError(
        errorData.error || `Request failed with status ${response.status}`,
        response.status
      );
    }

    const data = await response.json();
    const latencyMs = Date.now() - startTime;

    return {
      content: data.content || '',
      model: data.model || 'unknown',
      provider: data.provider || 'unknown',
      tokensUsed: undefined,
      latencyMs,
    };
  } catch (error) {
    if (error instanceof GACAError) {
      throw error;
    }
    throw new GACAError(
      error instanceof Error ? error.message : 'Network error connecting to G.A.C.A.',
      0
    );
  }
}

/**
 * Get the current status of G.A.C.A. providers
 */
export async function getGACAStatus(): Promise<GACAStatusResponse> {
  try {
    const response = await fetch('/api/health');

    if (!response.ok) {
      throw new GACAError(`Health check failed with status ${response.status}`, response.status);
    }

    const data = await response.json();
    const isOk = data.status === 'ok';

    return {
      status: isOk ? 'healthy' : 'down',
      activeProviders: isOk ? 1 : 0,
      totalProviders: 1,
      providers: [],
      timestamp: data.ts || new Date().toISOString(),
    };
  } catch (error) {
    return {
      status: 'down',
      activeProviders: 0,
      totalProviders: 1,
      providers: [],
      timestamp: new Date().toISOString(),
    };
  }
}

/**
 * Check G.A.C.A. health
 */
export async function getGACAHealth(): Promise<GACAHealthResponse> {
  try {
    const response = await fetch('/api/health');
    if (!response.ok) return { status: 'error' };
    const data = await response.json();
    return {
      status: data.status === 'ok' ? 'ok' : 'error',
      providersCount: 1,
      healthyProvidersCount: data.status === 'ok' ? 1 : 0,
    };
  } catch {
    return { status: 'error' };
  }
}

/**
 * Stream completion from G.A.C.A. (if supported)
 */
export async function* streamWithGACA(
  request: GACARequest
): AsyncGenerator<string, void, unknown> {
  try {
    const response = await fetch(`${GACA_API_URL}/api/chat/stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        prompt: request.prompt,
        systemPrompt: request.systemPrompt || getDefaultSystemPrompt(),
        maxTokens: request.maxTokens || 1024,
        temperature: request.temperature ?? 0.7,
      }),
    });

    if (!response.ok) {
      throw new GACAError(`Stream request failed`, response.status);
    }

    const reader = response.body?.getReader();
    if (!reader) {
      throw new GACAError('No response body');
    }

    const decoder = new TextDecoder();

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      const chunk = decoder.decode(value, { stream: true });
      yield chunk;
    }
  } catch (error) {
    if (error instanceof GACAError) {
      throw error;
    }
    throw new GACAError(
      error instanceof Error ? error.message : 'Stream error'
    );
  }
}

/**
 * Default system prompt for BiznesBezKlikania assistant
 */
function getDefaultSystemPrompt(): string {
  return `Jesteś asystentem AI dla BiznesBezKlikania.pl - platformy oferującej narzędzia do automatyzacji biznesu z wykorzystaniem sztucznej inteligencji.

Twoje główne zadania:
- Odpowiadaj na pytania dotyczące produktów i usług BiznesBezKlikania
- Pomagaj w doborze odpowiednich narzędzi AI do potrzeb użytkownika
- Wyjaśniaj jak działa automatyzacja procesów biznesowych
- Udzielaj informacji o cenach i dostępnych pakietach
- Kieruj do odpowiednich podstron z większą ilością informacji

Styl komunikacji:
- Pisz po polsku, profesjonalnie ale przyjaźnie
- Bądź konkretny i pomocny
- Jeśli nie znasz odpowiedzi, zaproponuj kontakt z zespołem
- Bez formatowania markdown — bez gwiazdek, bez nagłówków, bez list z myślnikami

Produkty BiznesBezKlikania:
- BiznesFinder AI - wyszukiwanie leadów i kontaktów biznesowych
- Agent WhatsApp AI - automatyczna obsługa klientów na WhatsApp
- SmartClaim AI - automatyzacja procesów reklamacyjnych
- ContentFlow AI - generowanie treści marketingowych
- I wiele innych narzędzi do automatyzacji`;
}

export default {
  completeWithGACA,
  getGACAStatus,
  getGACAHealth,
  streamWithGACA,
};
